/**
 * Runtime bridge script injected into generated project-app iframes.
 *
 * Generated pages consume the auth token in one of two styles:
 *   1. Window-event style (the documented contract): read
 *      window.__DREAM_AUTH_TOKEN__ after the 'dream-ready' window Event, and
 *      re-render on the 'dream-state-updated' CustomEvent.
 *   2. postMessage style: listen for 'message' events whose data is
 *      { type: 'dream-ready' | 'dream-state-updated', token }.
 *
 * The bridge signals both styles (mirroring the backend quick-view harness in
 * dream-be routes/global-apps-quick-view.ts) so either kind of page loads.
 */
export function buildGeneratedPageBridge(opts: {
  apiBase: string;
  appId: string;
  slug: string;
}): string {
  return `
<script>
(function() {
  window.__DREAM_API_BASE__ = ${JSON.stringify(opts.apiBase)};
  window.__DREAM_APP_ID__ = ${JSON.stringify(opts.appId)};
  window.__DREAM_VIEW_SLUG__ = ${JSON.stringify(opts.slug)};
  window.__DREAM_AUTH_TOKEN__ = null;

  // Listen for messages from parent
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'dream-auth' && typeof e.data.token === 'string') {
      window.__DREAM_AUTH_TOKEN__ = e.data.token;
      window.dispatchEvent(new Event('dream-ready'));
      // postMessage-style pages listen for a 'dream-ready' message instead.
      try { window.postMessage({ type: 'dream-ready', token: e.data.token }, '*'); } catch (_) {}
    }
    if (e.data && e.data.type === 'dream-state-refetch' && window.__DREAM_AUTH_TOKEN__) {
      fetch(window.__DREAM_API_BASE__ + '/project-apps/' + window.__DREAM_APP_ID__ + '/state', {
        headers: { Authorization: 'Bearer ' + window.__DREAM_AUTH_TOKEN__ }
      })
        .then(function(r) { return r.json(); })
        .then(function(json) {
          if (json && json.data) {
            window.dispatchEvent(new CustomEvent('dream-state-updated', { detail: json.data }));
            try { window.postMessage({ type: 'dream-state-updated', token: window.__DREAM_AUTH_TOKEN__, detail: json.data }, '*'); } catch (_) {}
          }
        })
        .catch(function() {});
    }
  });

  // Signal we're ready to receive the auth token. Retry a couple of times so
  // the parent React effect that attaches the message listener is guaranteed to
  // be running before we give up.
  function ready() {
    if (window.__DREAM_AUTH_TOKEN__) return;
    try { parent.postMessage({ type: 'dream-iframe-ready' }, '*'); } catch (_) {}
  }
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    ready();
  } else {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  }
  setTimeout(ready, 300);
  setTimeout(ready, 800);

})();
</script>`;
}

/** Inject the bridge as the first thing in <head>, or prepend if no <head>. */
export function wrapHtmlWithBridge(html: string, bridge: string): string {
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>${bridge}`);
  }
  return bridge + html;
}

/**
 * Dream 4.1 theme layer for generated pages.
 *
 * Generated HTML is self-contained (its own <style>, its own font links), so
 * the host's brand tokens never reach it. This stylesheet is appended to the
 * end of <head> — after the page's own styles — so its `:root` custom
 * properties win over the values a page (or the older house-style guide)
 * declared for the same names. It:
 *   - loads the brand fonts (Instrument Serif display, Instrument Sans body)
 *   - publishes the `--dream-*` semantic tokens (mirrors brand-tokens.scss)
 *   - remaps the legacy house-style variables (`--plus-white`, `--dream-blue`,
 *     …) onto the Dream 4.1 palette so pages generated against the old guide
 *     pick up the new palette without being regenerated
 *   - themes the page's scrollbars like the host
 */
export const GENERATED_PAGE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:ital,wght@0,400..700;1,400..700&display=swap';

export const GENERATED_PAGE_THEME_CSS = `
  :root {
    --dream-ink: #0d1f16;
    --dream-paper: #fffbf3;
    --dream-paper-2: #f5eedf;
    --dream-primary-green: #0a7a4a;
    --dream-marigold: #f2b630;
    --dream-marigold-light: #fdeec3;
    --dream-white: #ffffff;
    --dream-mid-grey: #4f5c54;
    --dream-light-grey: #87928b;
    --dream-lighter-grey: #e6e7ea;
    --dream-lightest-grey: #f8f8f9;
    --dream-light-paper: #fbfbf8;
    --dream-mid-paper: var(--dream-paper-2);
    --dream-dark-paper: #e5dbc2;
    --dream-red: #ef958b;
    --dream-red-light: #ffebe8;
    --dream-red-solid: #af3d36;
    --dream-green: #71c695;
    --dream-green-light: #dff8e7;
    --dream-gold: #e3b750;
    --dream-gold-light: #fcefd2;
    --dream-blue: #72b8f2;
    --dream-blue-light: #e4f2ff;
    --dream-blue-solid: #296898;

    --dream-bg-canvas: var(--dream-paper);
    --dream-bg-surface: var(--dream-light-paper);
    --dream-bg-surface-raised: var(--dream-white);
    --dream-bg-sidebar: var(--dream-white);
    --dream-bg-overlay: rgba(13, 31, 22, 0.7);
    --dream-bg-inverse: var(--dream-ink);
    --dream-surface-hover: rgba(13, 31, 22, 0.04);
    --dream-surface-pressed: rgba(13, 31, 22, 0.08);
    --dream-bg-scrim: rgba(13, 31, 22, 0.4);
    --dream-surface-inverse-hover: rgba(255, 251, 243, 0.1);
    --dream-gradient-ink: linear-gradient(135deg, #1c3328 0%, #0d1f16 100%);

    --dream-text-primary: var(--dream-ink);
    --dream-text-secondary: var(--dream-mid-grey);
    --dream-text-muted: var(--dream-light-grey);
    --dream-text-inverse: var(--dream-paper);
    --dream-text-inverse-secondary: rgba(255, 251, 243, 0.7);
    --dream-text-overlay: var(--dream-white);
    --dream-text-accent: var(--dream-primary-green);
    --dream-text-on-accent: var(--dream-paper);

    --dream-border: var(--dream-dark-paper);
    --dream-border-subtle: var(--dream-mid-paper);
    --dream-border-strong: var(--dream-ink);
    --dream-border-focus: var(--dream-primary-green);

    --dream-accent: var(--dream-primary-green);
    --dream-accent-hover: #08623b;
    --dream-accent-secondary: var(--dream-marigold);
    --dream-accent-subtle: var(--dream-marigold-light);

    --dream-success-bg: var(--dream-green-light);
    --dream-success-fg: #015632;
    --dream-success-border: var(--dream-green);
    --dream-success-solid: var(--dream-primary-green);
    --dream-warning-bg: var(--dream-gold-light);
    --dream-warning-fg: #624902;
    --dream-warning-border: var(--dream-gold);
    --dream-warning-solid: var(--dream-marigold);
    --dream-danger-bg: var(--dream-red-light);
    --dream-danger-fg: #72302a;
    --dream-danger-border: var(--dream-red);
    --dream-danger-solid: var(--dream-red-solid);
    --dream-info-bg: var(--dream-blue-light);
    --dream-info-fg: #0a4b77;
    --dream-info-border: var(--dream-blue);
    --dream-info-solid: var(--dream-blue-solid);

    --dream-tag-green: var(--dream-green);
    --dream-tag-red: var(--dream-red);
    --dream-tag-yellow: var(--dream-gold);
    --dream-tag-beige: var(--dream-dark-paper);
    --dream-tag-default: var(--dream-ink);
    --dream-tag-blue: var(--dream-blue);

    --dream-shadow-color: rgba(13, 31, 22, 0.08);
    --dream-shadow-sm: 0 1px 2px var(--dream-shadow-color);
    --dream-shadow-md: 0 1px 4px var(--dream-shadow-color);
    --dream-shadow-lg: 0 2px 4px rgba(13, 31, 22, 0.12), 0 8px 16px rgba(13, 31, 22, 0.08);
    --dream-shadow-card: 0 4px 8px rgba(13, 31, 22, 0.16), 0 16px 32px rgba(13, 31, 22, 0.2);
    --dream-scrollbar-thumb: rgba(13, 31, 22, 0.2);
    --dream-scrollbar-thumb-hover: rgba(13, 31, 22, 0.35);

    --dream-font-display: 'Instrument Serif', serif;
    --dream-font-body: 'Instrument Sans', sans-serif;

    /* Legacy house-style names → Dream 4.1 palette. */
    --plus-white: var(--dream-white);
    --classic-yellow: var(--dream-paper);
    --lisa-yellow: var(--dream-light-paper);
    --navbar-yellow: var(--dream-paper);
    --surface-dim: var(--dream-dark-paper);
    --surface-neutral: var(--dream-lighter-grey);
    --wallstreet-black: var(--dream-ink);
    --performa-black: var(--dream-mid-grey);
    --dark-grey: var(--dream-mid-grey);
    --muted-grey: var(--dream-light-grey);
    --quadra-grey: var(--dream-border);
    --light-grey: var(--dream-light-grey);
    --very-light-grey: var(--dream-mid-paper);
    --list-border: var(--dream-border);
    --dream-coral: var(--dream-accent);
    --dream-coral-light: var(--dream-green-light);
    --dream-violet: var(--dream-blue);
    --dream-violet-light: var(--dream-blue-light);
    --dream-yellow: var(--dream-marigold);
    --dream-orange: var(--dream-accent-hover);
    --dream-pink: var(--dream-green-light);
    --dream-sky: var(--dream-blue-light);
    --dark-blue: var(--dream-blue);
    --light-blue: var(--dream-blue-light);
    --light-green: var(--dream-success-fg);
    --dark-red: var(--dream-danger-fg);
    --warn: var(--dream-accent-hover);
  }

  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(13, 31, 22, 0.2) transparent;
  }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(13, 31, 22, 0.2); border-radius: 999px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(13, 31, 22, 0.35); }
  ::-webkit-scrollbar-button { display: none; width: 0; height: 0; }
`;

export function buildGeneratedPageTheme(): string {
  return `<link rel="stylesheet" href="${GENERATED_PAGE_FONTS_HREF}" /><style data-dream-theme>${GENERATED_PAGE_THEME_CSS}</style>`;
}

/** Legacy → Dream 4.1 family swap, applied inside CSS only. */
const FONT_FAMILY_SWAPS: ReadonlyArray<[RegExp, string]> = [
  [/(["']?)Plus Jakarta Sans\1/g, 'Instrument Serif'],
  [/(["']?)\bGeist\1(?![ +\w-]*Mono)/g, 'Instrument Sans'],
];

// Keeps whatever quoting the page used (a bare multi-word family is valid CSS).
const swapFamiliesInCss = (css: string): string =>
  FONT_FAMILY_SWAPS.reduce(
    (out, [pattern, family]) =>
      out.replace(pattern, (m, quote: string) => `${quote}${family}${quote}`),
    css,
  );

/**
 * Rewrites the families named in a page's CSS — <style> blocks and inline
 * `style=""` attributes: Plus Jakarta Sans → Instrument Serif, Geist → Instrument
 * Sans. Font <link> URLs, scripts and page copy are left alone.
 */
export function swapLegacyFonts(html: string): string {
  return html
    .replace(
      /(<style\b[^>]*>)([\s\S]*?)(<\/style>)/gi,
      (m, open, css, close) => `${open}${swapFamiliesInCss(css)}${close}`,
    )
    .replace(
      /(\sstyle\s*=\s*)("[^"]*"|'[^']*')/gi,
      (m, attr, value) => `${attr}${swapFamiliesInCss(value)}`,
    );
}

/**
 * Applies the Dream 4.1 theme to a generated page: swaps legacy families and
 * appends the theme layer at the end of <head> (or the end of the document
 * when there is none) so it cascades after the page's own styles.
 */
export function themeGeneratedPage(html: string): string {
  const swapped = swapLegacyFonts(html);
  const theme = buildGeneratedPageTheme();
  if (/<\/head>/i.test(swapped)) {
    return swapped.replace(/<\/head>/i, `${theme}</head>`);
  }
  if (/<body[^>]*>/i.test(swapped)) {
    return swapped.replace(/<body([^>]*)>/i, `${theme}<body$1>`);
  }
  return theme + swapped;
}
